import { useEffect, useRef, useState } from "react";

/** True when the OS asks for reduced motion (or `window` is unavailable). */
export function prefersReduced(): boolean {
  return (
    typeof window !== "undefined" &&
    (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false)
  );
}

/** Logo block + card lift once the glows have spread… */
const INTRO_MS = 1300;
/** …and the slogan only writes itself out once that lift has finished. Mirrors
 *  the logo block's 800ms transition — keep the two in sync. */
const LIFT_MS = 800;

export type LoginIntro = {
  /** Honours `prefers-reduced-motion` — when true the page skips straight to
   *  the settled state and the cursor parallax is inert. */
  reduced: boolean;
  /** Drives the glows from centre → corners. */
  spread: boolean;
  /** Logo block lifted + card revealed. */
  ready: boolean;
  /** Slogan eased in *after* the lift (drives the glyph write-on). */
  taglineIn: boolean;
  /** Cursor parallax offset, normalised to [-1, 1] per axis. */
  par: { x: number; y: number };
  /** Mouse-move handler for the parallax (no-op under reduced motion). */
  onMove: (e: React.MouseEvent) => void;
};

/**
 * The sign-in screen's entrance choreography, extracted from `LoginPage.tsx`:
 * the glows spread out of centre, the logo + card lift in, then the slogan
 * writes itself out — all gated behind `prefers-reduced-motion`. The glows
 * keep tracking the cursor via `par` + `onMove`.
 */
export function useLoginIntro(): LoginIntro {
  const reducedInit = prefersReduced();
  const reduced = useRef(reducedInit);
  // `spread` drives the glows from centre → corners; `phase` lifts the logo
  // block + reveals the card; `taglineIn` eases the slogan in *after* the lift.
  const [spread, setSpread] = useState(reducedInit);
  const [phase, setPhase] = useState<"intro" | "ready">(reducedInit ? "ready" : "intro");
  const [taglineIn, setTaglineIn] = useState(reducedInit);
  const [par, setPar] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (reduced.current) return;
    const raf = requestAnimationFrame(() => setSpread(true));
    const tReady = window.setTimeout(() => setPhase("ready"), INTRO_MS);
    // Hold the slogan back until the logo + card have finished sliding up.
    const tTagline = window.setTimeout(() => setTaglineIn(true), INTRO_MS + LIFT_MS);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(tReady);
      window.clearTimeout(tTagline);
    };
  }, []);

  const onMove = (e: React.MouseEvent) => {
    if (reduced.current) return;
    setPar({
      x: (e.clientX / window.innerWidth - 0.5) * 2,
      y: (e.clientY / window.innerHeight - 0.5) * 2,
    });
  };

  return { reduced: reduced.current, spread, ready: phase === "ready", taglineIn, par, onMove };
}
